import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map } from 'rxjs';
import { RecordPaymentDialogComponent } from '../dialogs/record-payment/record-payment.dialog';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { InvoiceInterface, InvoiceStatus } from '@contracts/interfaces/invoice.interface';
import { ConnectionService } from './connection.service';
import { InvoiceFactory } from '@contracts/factories/invoice.factory';
import { InvoiceLineItemFindDialogComponent } from '../dialogs/invoice-line-item-find/invoice-line-item-find.dialog';
import { LineItem } from '@contracts/interfaces/lineitem.interface';
import { InvoiceDialogComponent } from '../dialogs/invoice-dialog/invoice.dialog';

@Injectable({
  providedIn: 'root'
})
export class InvoicesService {
 
  public genericLineItems: LineItem[] = [];

  constructor(private http: HttpClient,
    private connectionService: ConnectionService,
    public dialog: MatDialog) {}

  get(invoiceNumber: number | null) {
    return this.http.post(this.connectionService.address + "/invoice-service/get", {invoiceNumber}).pipe(
      map((r: InvoiceInterface) => {
        if (r != null) {
          return InvoiceFactory.validate(r);
        }
      })
    );
  }
  
  find(query: any = {}, status?: string | string[], includeReconciliation = false) {
    return this.http.post(this.connectionService.address + "/invoice-service/find", { query, status, includeReconciliation }).pipe(
      map((invoices: InvoiceInterface[]) => {
        for (let i=0; i < invoices.length;i++) {
          invoices[i] = InvoiceFactory.validate(invoices[i]);
        }
        return invoices;
      })
    );
  }

  getInfo(query: any = {}) {
    return this.http.post(this.connectionService.address + "/invoice-service/get-info", { query });
  }

  getPayments(query: any = {}, paymentMethod?: string | null) {
    const payload: any = { query };
    if (paymentMethod) {
      payload.paymentMethod = paymentMethod;
    }
    return this.http.post(this.connectionService.address + "/invoice-service/get-payments", payload);
  }

  linkPaymentToTransaction(invoiceNumber: number, paymentId: string | undefined, transactionSha1: string, paymentIndex?: number) {
    return this.http.post(this.connectionService.address + "/invoice-service/link-payment-transaction", {
      invoiceNumber,
      paymentId,
      paymentIndex,
      transactionSha1
    });
  }

  validateAll(query: any = {}) {
    return this.http.post(this.connectionService.address + "/invoice-service/validate-all", { query });
  }

  getStatusValues(query: any = {}) {
    return this.http.post(this.connectionService.address + "/invoice-service/status-values", { query });
  }

  create(invoice: InvoiceInterface) {
    return this.http.post(this.connectionService.address + "/invoice-service/create", { invoice });
  }

  updateOne(invoice: InvoiceInterface) {
    return this.http.post(this.connectionService.address + "/invoice-service/update-one", { invoice });
  }

  deleteOne(invoice: InvoiceInterface | number) {
    const invoicePayload = typeof invoice === 'number'
      ? { number: invoice }
      : { number: invoice?.number };

    return this.http.post(this.connectionService.address + "/invoice-service/delete-one", { invoice: invoicePayload });
  }

  getAvailableInvoiceNumber() {
    return this.http.post(this.connectionService.address + "/invoice-service/get-available-invoice-number", {});
  }

  recordPayment(invoice: InvoiceInterface, callback: any) {
    return this.get(invoice.number).subscribe((result) => {
      let dialog = this.dialog.open(RecordPaymentDialogComponent, {
          autoFocus: false,
          data: {
              invoice: result || invoice
          }
      });

      dialog.afterClosed().subscribe(r => {
          callback(r);
      })
    });
  }

  newInvoice (type: string, customer: CustomerInterface): InvoiceInterface {
    return {
      type: type,
      status: InvoiceStatus.Unpaid,
      customer: customer,
      number: null,
      custom: "",
      dateCreated: new Date(),
      dateDue: new Date(),
      subTotal: 0,
      total: 0,
      paymentsMade: [],
      pdf: {
        file: "invoice-" + type
      },
      lineItems: [
        {
          item: '',
          desc: '',
          qty: 1,
          price: 0
        }
      ],
      gst: 0,
      isGSTIncluded: true,
      communicationLog: []
    }
  }

  validate(invoice: InvoiceInterface | any) {
    if (!invoice) {
      return InvoiceFactory.validate(this.newInvoice("standard", null));
    }

    invoice.lineItems = invoice.lineItems || [];
    invoice.paymentsMade = invoice.paymentsMade || [];
    invoice.total = 0;
    invoice.gst = 0;
    invoice.gstEx = 0;
    invoice.subTotal = 0;

    for (let i=0; i < invoice.lineItems.length; i++) {
      let lineTotal = (+invoice.lineItems[i].price || 0) * (+invoice.lineItems[i].qty || 0);
      invoice.lineItems[i].total = lineTotal;

      if (invoice.isGSTIncluded) {
        invoice.subTotal += (lineTotal - (lineTotal / 11));
        invoice.gst += lineTotal / 11;
        invoice.total += lineTotal;
      } else {
        invoice.subTotal += lineTotal;
        invoice.gst += lineTotal * 0.1;
        invoice.total += (lineTotal + (lineTotal * 0.1));
      }
    }

    let amountDue = invoice.total;

    // Calculate Due Amount
    let didDoRefund = false;
    for (let paymentMade of invoice.paymentsMade) {
      if (paymentMade.amount > 0) {
        amountDue -= +paymentMade.amount;
        paymentMade.type = "Payment";
      } else if (paymentMade.amount < 0) {
        didDoRefund = true;
        paymentMade.type = "Refund";
      } 
    }

    invoice.amountDue = amountDue;

    // Determine status
    if (amountDue > 0) {
      invoice.status = "unpaid";
    } else {
      invoice.status = "paid";      
    }

    if (didDoRefund) {
      invoice.status = "refund";
    }

    return invoice;
  }

  openLineItemFindDialog(prefillLineItems, callback: any) {
    let dialog = this.dialog.open(InvoiceLineItemFindDialogComponent, {
      width: "100%",
      maxWidth: "600px",
      autoFocus: true,
      data: {
        prefillLineItems
      }
    });

    dialog.afterClosed().subscribe((r: any) => {
      callback(r);
    })
  }

  public openRecordPaymentDialog(invoice: InvoiceInterface, refund: boolean = false, callback: any) {
    return this.get(invoice.number).subscribe((result) => {
        let dialog = this.dialog.open(RecordPaymentDialogComponent, {
            autoFocus: false,
            data: {
                invoice: result || invoice,
                refund
            }
        });

        dialog.afterClosed().subscribe(r => {
            callback(r);
        })
    });
  }

  openInvoiceDialog(invoice: InvoiceInterface, callback: any) {
    this.openInvoiceDialogPrivate({
      invoice
    }, callback);
  }

  openCreateInvoiceDialog(type: string, callback: any) {
    this.openInvoiceDialogPrivate({
      type
    }, callback);
  }

  private openInvoiceDialogPrivate(data: any, callback: any) {
    let dialog = this.dialog.open(InvoiceDialogComponent, {
      width: '1250px',
      height: '90vh',
      autoFocus: false,
      data
    });

    dialog.afterClosed().subscribe((r: any) => {
      callback(r);
    })
  }

}
